import "@abraham/reflection";

import "./themes/default/default.css";
import "./themes/default_v2/default.css";
import "./themes/globals.css";

import {
    GiftRegistryApplicationConfigInterface,
    GiftRegistryApplicationConfigToken,
} from "@apps/gift_registry/GiftRegistryApplicationConfig";
import {
    IloomCustomizationApplicationConfigInterface,
    IloomCustomizationApplicationConfigToken,
} from "@apps/iloom_customization/IloomCustomizationApplicationConfig";
import {
    InstagramApplicationConfigInterface,
    InstagramApplicationConfigToken,
} from "@apps/instagram/InstagramApplicationConfig";
import {
    MediaFeedApplicationConfigInterface,
    MediaFeedApplicationConfigToken,
} from "@apps/media_feed/MediaFeedApplicationConfig";
import { ReviewsApplicationConfigInterface } from "@apps/reviews/ReviewsApplicationConfig";
import { ReviewsApplicationConfigToken } from "@apps/reviews/tokens";
import { REWARDS_WIDGETS_PLACEHOLDERS } from "@apps/rewards/constants/constants";
import {
    RewardsApplicationConfigInterface,
    RewardsApplicationConfigToken,
} from "@apps/rewards/RewardsApplicationConfig";
import {
    TikTokApplicationConfigInterface,
    TikTokApplicationConfigToken,
} from "@apps/tiktok/TikTokApplicationConfig";
import {
    WishlistApplicationConfigInterface,
    WishlistApplicationConfigToken,
} from "@apps/wishlist/WishlistApplicationConfig";
import CustomerInfoFromJsonProvider from "@lib/CustomerInfoProvider/CustomerInfoFromJsonProvider";
import CustomerInfoFromStaticProvider from "@lib/CustomerInfoProvider/CustomerInfoFromStaticProvider";
import GrowaveConfigurationFromJsonProvider from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationFromJsonProvider";
import GrowaveConfigurationFromStaticProvider from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationFromStaticProvider";
import HistoryPatcher from "@lib/HistoryPatcher";
import { LegacyAdapter } from "@lib/LegacyAdapter/LegacyAdapter";
import { SimpleLocalizationInfoProvider } from "@lib/LocalizationInfoProvider/SimpleLocalizationInfoProvider";
import { ProductIdFromAdditionalInfoProvider } from "@lib/ProductIdManager/ProductIdFromAdditionalInfoProvider";
import { ProductIdFromSdkProvider } from "@lib/ProductIdManager/ProductIdFromSdkProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import { ProductVariantIdFromAdditionalInfoProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromAdditionalInfoProvider";
import { ProductVariantIdFromSdkProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromSdkProvider";
import { ProductVariantIdFromUrlProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromUrlProvider";
import { ProductVariantsIdManager } from "@lib/ProductVariantIdManager/ProductVariantIdManager";
import ShopBrandInfoFromJsonProvider from "@lib/ShopBrandInfoProvider/ShopBrandInfoFromJsonProvider";
import ShopBrandInfoFromStaticProvider from "@lib/ShopBrandInfoProvider/ShopBrandInfoFromStaticProvider";
import StorefrontApiAccessTokenProvider from "@lib/StorefrontApiAccessTokenProvider/StorefrontApiAccessTokenProvider";
import Timer from "@lib/timer";
import { getJsonFromScript } from "@lib/utils";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { NudgesApplicationConfigInterface } from "@modules/nudges/NudgesApplicationConfig";
import { NudgesApplicationConfigToken } from "@modules/nudges/tokens";
import { ProductsModule } from "@modules/products/ProductsModule";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { SdkModule } from "@modules/sdk/SdkModule";
import { SdkInitOptions } from "@modules/sdk/types";
import AttributesObserver from "@ui-utils/AttributesObserver/AttributesObserver";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { validate } from "superstruct";
import {
    DependencyContainer,
    container,
    instanceCachingFactory,
} from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";

import { App, GW_ADDITIONAL_INFO_SELECTOR } from "./constants";
import { CURRENT_APP_GROWAVE } from "./constants/current_app";
import { GP_GW_REFFERER } from "./constants/get_params";
import InitOptionsEntity from "./entities/InitOptionsEntity";
import { MinilogLogger } from "./lib/Logger";
import InitOptionsSchema from "./schemas/InitOptionsSchema";
import {
    customerInfoProviderToken,
    dispatchSdkEventToken,
    globalLoggerToken,
    globalProductIdManagerToken,
    globalProductVariantsIdManagerToken,
    growaveConfigurationProviderToken,
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    isEmbeddedModeToken,
    localStorageToken,
    localizationInfoProviderToken,
    modalManagerToken,
    shopBrandInfoProviderToken,
    storefrontApiAccessTokenProviderToken,
} from "./tokens";
import { checkIsEmbeddedMode } from "./utils/checkIsEmbeddedMode";
import { registerStyleBasedCustomizations } from "./utils/registerStyleBasedCustomizations/registerStyleBasedCustomizations";

registerStyleBasedCustomizations();

interface InitOptions {
    enabledApps: App[];
    customerId: number | null;
    customerEmail: string | null;
    customerFirstName: string | null;
    customerLastName: string | null;
    isB2bCustomer: boolean;
    customerTags: string[];
    moneyFormat: string;
    moneyWithCurrencyFormat: string;
    shopCurrency: string;
    currencySymbol: string;
    storefrontApiAccessToken: string | null;
    countryIsoCode: string;
    languageIsoCode: string;
    currencyIsoCode: string;
    gwI18nLocale: string;
    shopName: string;
    isShadowRootDisabledGlobally: boolean;
    modifyURL?: (url: string) => string;
}

// eslint-disable-next-line @typescript-eslint/require-await
async function init(options: InitOptions) {
    const globalLogger = container.resolve(globalLoggerToken);
    const [initOptionsError] = validate(options, InitOptionsSchema);

    if (initOptionsError) {
        globalLogger.error("Invalid init options", {
            initOptionsError,
            options,
        });
        throw new Error("Invalid init options");
    }

    const historyPatcher = new HistoryPatcher(
        [GP_GW_REFFERER],
        window.history,
        container.resolve(CommonModule).exports.locationController
    );
    historyPatcher.patch();

    container.register(localizationInfoProviderToken, {
        useFactory: instanceCachingFactory((di) => {
            return new SimpleLocalizationInfoProvider(
                di.resolve(growaveConfigurationToken).countryIsoCode,
                di.resolve(growaveConfigurationToken).languageIsoCode,
                di.resolve(growaveConfigurationToken).currencyIsoCode,
                di.resolve(growaveConfigurationToken).gwI18nLocale
            );
        }),
    });

    container.resolve(AnalyticsEventsCollectorModule).exports.startSending();
    container.resolve(AuthenticationModule);
    container.resolve(ProductsModule);
    container.resolve(LegacyAdapter);
    container.register(MutationObserverInjector, {
        useFactory: instanceCachingFactory(() => {
            return new MutationObserverInjector(
                MutationObserver,
                document.body
            );
        }),
    });
    container.register(AttributesObserver, {
        useFactory: instanceCachingFactory(() => {
            return new AttributesObserver(MutationObserver);
        }),
    });

    // Design Mode
    const isDesignMode = container
        .resolve(CommonModule)
        .exports.designModeProvider.isDesignMode();

    // ModalManager
    container.register(modalManagerToken, {
        useFactory: instanceCachingFactory(() => {
            return new ModalManager(dispatchSdkEvent);
        }),
    });

    container.register(dispatchSdkEventToken, { useValue: dispatchSdkEvent });

    globalLogger.debug("start growave initialization");

    container
        .resolve(AnalyticsEventsCollectorModule)
        .exports.eventsCollector.pushEvent(
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsFactory.createChunkLoaded("main", -1)
        );

    if (options.enabledApps.includes(App.GiftRegistry)) {
        container.register<GiftRegistryApplicationConfigInterface>(
            GiftRegistryApplicationConfigToken,
            {
                useValue: {
                    addToRegistryProductPageWidgetPlaceholder:
                        ".gw-gr-add-to-registry-product-page-widget-placeholder",
                    sharedRegistryPageWidgetPlaceholder:
                        ".gw-gr-shared-registry-page-widget-placeholder",
                    addToRegistryModalWidgetPlaceholder: "body",
                    registryFormModalWidgetPlaceholder: "body",
                    giftCartDrawerWidgetPlaceholder: "body",
                },
            }
        );

        const startLoadingGiftRegistryApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "GiftRegistryApplication"*/ "@apps/gift_registry/GiftRegistryApplication"
        ).then(({ GiftRegistryApplication }) => {
            const duration = startLoadingGiftRegistryApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "GiftRegistryApplication",
                            duration
                        )
                );
            const giftRegistryApplication = container.resolve(
                GiftRegistryApplication
            );
            giftRegistryApplication.init();
        });
    }

    if (options.enabledApps.includes(App.Instagram) || isDesignMode) {
        container.register<InstagramApplicationConfigInterface>(
            InstagramApplicationConfigToken,
            {
                useValue: {
                    placeholderSelector: ".gw-instagram-gallery-placeholder",
                },
            }
        );
        const startLoadingInstagramApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "InstagramApplication"*/ "@apps/instagram/InstagramApplication"
        ).then(({ InstagramApplication }) => {
            const duration = startLoadingInstagramApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "InstagramApplication",
                            duration
                        )
                );
            const instagramApplication =
                container.resolve(InstagramApplication);
            instagramApplication.init();
        });
    }

    // NOTE: Temporarily disabling the App.Login
    const ENABLE_LOGIN_APP = false;

    if (ENABLE_LOGIN_APP && options.enabledApps.includes(App.Login)) {
        const startLoadingLoginApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "LoginApplication"*/ "@apps/login/LoginApplication"
        ).then(({ LoginApplication }) => {
            const duration = startLoadingLoginApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "LoginApplication",
                            duration
                        )
                );

            const loginApplication = container.resolve(LoginApplication);
            loginApplication.init();
        });
    }

    if (options.enabledApps.includes(App.TikTok) || isDesignMode) {
        container.register<TikTokApplicationConfigInterface>(
            TikTokApplicationConfigToken,
            {
                useValue: {
                    placeholderSelector: "#gw-tiktok-gallery-placeholder",
                },
            }
        );
        const startLoadingTikTokApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "TikTokApplication" */ "@apps/tiktok/TikTokApplication"
        ).then(({ TikTokApplication }) => {
            const duration = startLoadingTikTokApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "TikTokApplication",
                            duration
                        )
                );
            const tiktokApplication = container.resolve(TikTokApplication);
            tiktokApplication.init();
        });
    }

    if (
        options.enabledApps.includes(App.Wishlist) ||
        options.enabledApps.includes(App.GiftRegistry) ||
        isDesignMode
    ) {
        container.register<WishlistApplicationConfigInterface>(
            WishlistApplicationConfigToken,
            {
                useValue: {
                    wishlistsWidgetPlaceholder: ".gw-wl-widget-placeholder",
                    drawerWidgetPlaceholderSelector: "body",
                    addToWishlistProductPageButtonSelector:
                        ".gw-wl-add-to-wishlist-placeholder",
                    addToWishlistProductCardSelector:
                        ".gw-add-to-wishlist-product-card-placeholder",
                    goToWishlistPageHeaderWidgetInjector:
                        ".gw-wl-header-icon-placeholder",
                    wishlistPageWidgetPlaceholderSelector:
                        ".gw-wl-page-placeholder",
                    sharedWishlistPageWidgetPlaceholderSelector:
                        ".gw-wl-shared-page-placeholder",
                    saveWishlistModalWidgetPlaceholderSelector: "body",
                    wishlistsManageApproversModalWidgetPlaceholderSelector:
                        "body",
                    wishlistsItemsTotalCountInlineWidgetPlaceholderSelector:
                        ".gw-wl-items-total-count-placeholder",
                    saveForLaterModalWidgetPlaceholderSelector:
                        ".gw-wl-save-for-later-modal-placeholder",
                },
            }
        );

        const startLoadingWishlistApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "WishlistApplication" */ "@apps/wishlist/WishlistApplication"
        ).then(({ WishlistApplication }) => {
            const duration = startLoadingWishlistApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "WishlistApplication",
                            duration
                        )
                );
            const wishlistApplication = container.resolve(WishlistApplication);
            wishlistApplication.init();
        });
    }

    if (
        options.enabledApps.includes(App.Reviews) ||
        options.enabledApps.includes(App.Questions) ||
        isDesignMode
    ) {
        // reviews
        container.registerInstance<ReviewsApplicationConfigInterface>(
            ReviewsApplicationConfigToken,
            {
                reviewsWidgetPlaceholder: ".gw-rv-widget-placeholder",
                drawerWidgetPlaceholderSelector: "body",
                averageWidgetPlaceholderSelector: ".gw-rv-average-placeholder",
                reviewsStarRatingWidgetPlaceholderSelector:
                    ".gw-rv-star-rating-widget-placeholder",
                listingAverageReviewsWidgetPlaceholderSelector:
                    ".gw-rv-listing-average-placeholder",
                reviewsProductPageWidgetPlaceholderSelector:
                    ".gw-rv-main-widget-placeholder",
                reviewsPageWidgetPlaceholderSelector:
                    ".gw-rv-reviews-page-widget-placeholder",
                reviewsSliderWidgetPlaceholderSelector:
                    ".gw-rv-reviews-slider-widget-placeholder",
                reviewsGalleryWidgetPlaceholderSelector: "body",
                productReviewsMiniSliderWidgetPlaceholderSelector:
                    ".gw-rv-mini-slider-widget-placeholder",
                reviewsFormModalWidgetPlaceholderSelector: "body",
                reviewsEditFormModalWidgetPlaceholderSelector: "body",
                questionsFormModalWidgetPlaceholderSelector: "body",
                reviewsListHeaderWidgetPlaceholderSelector:
                    ".gw-rv-reviews-list-header-widget-placeholder",
                reviewsListWidgetPlaceholderSelector:
                    ".gw-rv-reviews-list-widget-placeholder",
                questionsListWidgetPlaceholderSelector:
                    ".gw-rv-questions-list-widget-placeholder",
                reviewsTrustBadgeWidgetPlaceholderSelector:
                    ".gw-rv-trust-badge-widget-placeholder",
            }
        );

        const startLoadingReviewsApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "ReviewsApplication" */ "@apps/reviews/ReviewsApplication"
        ).then(({ ReviewsApplication }) => {
            const duration = startLoadingReviewsApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "ReviewsApplication",
                            duration
                        )
                );
            const reviewsApplication = container.resolve(ReviewsApplication);
            reviewsApplication.init();
        });
    }

    {
        container.registerInstance<NudgesApplicationConfigInterface>(
            NudgesApplicationConfigToken,
            {
                nudgesWidgetPlaceholderSelector: "body",
            }
        );

        const startLoadingNudgesApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "NudgesApplication" */ "@modules/nudges/NudgesApplication"
        ).then(({ NudgesApplication }) => {
            const duration = startLoadingNudgesApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "NudgesApplication",
                            duration
                        )
                );
            const nudgesApplication = container.resolve(NudgesApplication);
            nudgesApplication.init();
        });
    }

    if (options.enabledApps.includes(App.IloomCustomization)) {
        container.register<IloomCustomizationApplicationConfigInterface>(
            IloomCustomizationApplicationConfigToken,
            {
                useValue: {
                    wishlistPageWidgetPlaceholderSelector:
                        ".gw-cwl-page-placeholder",
                    reviewsPageWidgetPlaceholderSelector:
                        ".gw-crv-page-placeholder",
                },
            }
        );

        const startLoadingIloomCustomizationApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "IloomCustomizationApplication" */ "@apps/iloom_customization/IloomCustomizationApplication"
        ).then(({ IloomCustomizationApplication }) => {
            const duration =
                startLoadingIloomCustomizationApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "IloomCustomizationApplication",
                            duration
                        )
                );
            const iloomCustomizationApplication = container.resolve(
                IloomCustomizationApplication
            );
            iloomCustomizationApplication.init();
        });
    }
    // todo iloom заменить на App.MediaFeed после добавления пермишена
    if (options.enabledApps.includes(App.IloomCustomization)) {
        container.register<MediaFeedApplicationConfigInterface>(
            MediaFeedApplicationConfigToken,
            {
                useValue: {
                    // todo iloom заменить на gw-mf
                    mediaFeedPageWidgetPlaceholderSelector:
                        ".gw-df-page-placeholder",
                    mediaFeedHomeWidgetPlaceholderSelector:
                        ".gw-df-home-placeholder",
                    mediaFeedProductWidgetPlaceholderSelector:
                        ".gw-df-product-placeholder",
                },
            }
        );

        const startLoadingMediaFeedApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "MediaFeedApplication" */ "@apps/media_feed/MediaFeedApplication"
        ).then(({ MediaFeedApplication }) => {
            const duration = startLoadingMediaFeedApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "MediaFeedApplication",
                            duration
                        )
                );
            const mediaFeedApplication =
                container.resolve(MediaFeedApplication);
            mediaFeedApplication.init();
        });
    }

    if (options.enabledApps.includes(App.Rewards) || isDesignMode) {
        container.register<RewardsApplicationConfigInterface>(
            RewardsApplicationConfigToken,
            {
                useValue: REWARDS_WIDGETS_PLACEHOLDERS,
            }
        );

        const startLoadingRewardsApplicationTimer = new Timer();
        void import(
            /*webpackChunkName: "RewardsApplication"*/ "@apps/rewards/RewardsApplication"
        ).then(({ RewardsApplication }) => {
            const duration = startLoadingRewardsApplicationTimer.ready();
            container
                .resolve(AnalyticsEventsCollectorModule)
                .exports.eventsCollector.pushEvent(
                    container
                        .resolve(AnalyticsEventsCollectorModule)
                        .exports.eventsFactory.createChunkLoaded(
                            "RewardsApplication",
                            duration
                        )
                );
            const rewardsApplication = container.resolve(RewardsApplication);
            rewardsApplication.init();
        });
    }
}

async function initFromAdditionalInfo(additionalInfoData: unknown) {
    const globalLogger = container.resolve(globalLoggerToken);

    // (utils) customerInfoProvider
    const customerInfoProvider = new CustomerInfoFromJsonProvider(
        globalLogger,
        additionalInfoData
    );
    customerInfoProvider.readData();
    container.registerInstance(customerInfoProviderToken, customerInfoProvider);

    // (utils) shopBrandInfoProvider
    container.register(shopBrandInfoProviderToken, {
        useFactory: instanceCachingFactory(() => {
            const shopBrandInfoFromJsonProvider =
                new ShopBrandInfoFromJsonProvider(
                    container.resolve(globalLoggerToken),
                    additionalInfoData
                );
            shopBrandInfoFromJsonProvider.readData();
            return shopBrandInfoFromJsonProvider;
        }),
    });

    // (utils) growaveConfigurationProvider
    container.register(growaveConfigurationProviderToken, {
        useFactory: instanceCachingFactory(() => {
            return new GrowaveConfigurationFromJsonProvider(
                container.resolve(globalLoggerToken),
                additionalInfoData
            );
        }),
    });

    container.register(growaveConfigurationToken, {
        useFactory: instanceCachingFactory((di: DependencyContainer) => {
            return di
                .resolve(growaveConfigurationProviderToken)
                .getConfiguration();
        }),
    });

    const growaveConfiguration = container.resolve(growaveConfigurationToken);

    const initOptions: InitOptionsEntity = {
        enabledApps: growaveConfiguration.availableApps,
        customerId: customerInfoProvider.getId(),
        customerEmail: customerInfoProvider.getEmail(),
        customerFirstName: customerInfoProvider.getFirstName(),
        customerLastName: customerInfoProvider.getLastName(),
        isB2bCustomer: customerInfoProvider.getIsB2b(),
        customerTags: customerInfoProvider.getTags(),
        moneyFormat: growaveConfiguration.moneyFormat,
        moneyWithCurrencyFormat: growaveConfiguration.moneyWithCurrencyFormat,
        shopCurrency: growaveConfiguration.shopCurrency,
        currencySymbol: growaveConfiguration.currencySymbol,
        storefrontApiAccessToken: growaveConfiguration.storefrontApiAccessToken,
        countryIsoCode: growaveConfiguration.countryIsoCode,
        languageIsoCode: growaveConfiguration.languageIsoCode,
        currencyIsoCode: growaveConfiguration.currencyIsoCode,
        gwI18nLocale: growaveConfiguration.gwI18nLocale,
        shopName: growaveConfiguration.shopName,
        isShadowRootDisabledGlobally:
            growaveConfiguration.isShadowRootDisabledGlobally,
        isDiscountApplyAvailable: true,
        isCartAvailable: true,
    };

    await init(initOptions);
}

export function initFromSdk(options: SdkInitOptions) {
    const isEmbeddedMode = container.resolve(isEmbeddedModeToken);

    if (isEmbeddedMode) {
        throw new Error(
            "Initialization from the SDK is not supported in embedded mode"
        );
    }

    const globalLogger = container.resolve(globalLoggerToken);

    // (utils) customerInfoProvider
    const customerInfoProvider = new CustomerInfoFromStaticProvider(
        globalLogger,
        {
            id: null,
            email: null,
            firstName: null,
            lastName: null,
            isB2b: false,
            tags: [],
            company: null,
        }
    );
    customerInfoProvider.readData();
    container.registerInstance(customerInfoProviderToken, customerInfoProvider);

    // (utils) shopBrandInfoProvider
    container.register(shopBrandInfoProviderToken, {
        useFactory: instanceCachingFactory(() => {
            const shopBrandInfoFromStaticProvider =
                new ShopBrandInfoFromStaticProvider(
                    container.resolve(globalLoggerToken),
                    {
                        logoUrl: null,
                    }
                );
            shopBrandInfoFromStaticProvider.readData();
            return shopBrandInfoFromStaticProvider;
        }),
    });

    // (utils) storefrontApiAccessTokenProvider
    container.register(storefrontApiAccessTokenProviderToken, {
        useFactory: instanceCachingFactory((di) => {
            return new StorefrontApiAccessTokenProvider(
                di.resolve(globalLoggerToken)
            );
        }),
    });

    const storefrontApiAccessToken = container
        .resolve(storefrontApiAccessTokenProviderToken)
        .getToken();

    // (utils) growaveConfigurationProvider
    const growaveConfigurationProvider =
        new GrowaveConfigurationFromStaticProvider(globalLogger, {
            moneyFormat: options.moneyFormat || "{{amount}}",
            moneyWithCurrencyFormat:
                options.moneyWithCurrencyFormat || "{{amount}}",
            shopCurrency: options.shopCurrency || "USD",
            currencySymbol: options.currencySymbol || "$",
            availableApps: options.enabledApps,
            storefrontApiAccessToken:
                storefrontApiAccessToken ||
                options.storefrontApiAccessToken ||
                null,
            countryIsoCode: options.countryIsoCode || "US",
            languageIsoCode: options.languageIsoCode || "EN",
            currencyIsoCode: options.currencyIsoCode || "USD",
            gwI18nLocale: options.localization || "en",
            shopName: options.shopName || "",
            isShadowRootDisabledGlobally:
                options.isShadowRootDisabledGlobally || false,
            myshopifyDomain:
                options.myshopifyDomain || (window.Shopify?.shop as string),
            initedFromSdk: true,
            isDiscountApplyAvailable: options.isDiscountApplyAvailable || true,
            isCartAvailable: options.isCartAvailable || true,
        });
    const growaveConfiguration =
        growaveConfigurationProvider.getConfiguration();
    container.registerInstance(
        growaveConfigurationProviderToken,
        growaveConfigurationProvider
    );

    container.registerInstance(growaveConfigurationToken, growaveConfiguration);

    const initOptions: InitOptionsEntity = {
        enabledApps: options.enabledApps,
        customerId: customerInfoProvider.getId(),
        customerEmail: customerInfoProvider.getEmail(),
        customerFirstName: customerInfoProvider.getFirstName(),
        customerLastName: customerInfoProvider.getLastName(),
        isB2bCustomer: customerInfoProvider.getIsB2b(),
        customerTags: customerInfoProvider.getTags(),
        moneyFormat: growaveConfiguration.moneyFormat,
        moneyWithCurrencyFormat: growaveConfiguration.moneyWithCurrencyFormat,
        shopCurrency: growaveConfiguration.shopCurrency,
        currencySymbol: growaveConfiguration.currencySymbol,
        storefrontApiAccessToken: growaveConfiguration.storefrontApiAccessToken,
        countryIsoCode: growaveConfiguration.countryIsoCode,
        languageIsoCode: growaveConfiguration.languageIsoCode,
        currencyIsoCode: growaveConfiguration.currencyIsoCode,
        gwI18nLocale: growaveConfiguration.gwI18nLocale,
        shopName: growaveConfiguration.shopName,
        isShadowRootDisabledGlobally:
            growaveConfiguration.isShadowRootDisabledGlobally,
        isDiscountApplyAvailable: true,
        isCartAvailable: true,
    };

    void init(initOptions);
}

function __gwMain() {
    // Initialize window.gw object
    if (!window.gw) {
        window.gw = {};
    }

    container.register(globalLoggerToken, {
        useFactory: instanceCachingFactory(() => {
            return new MinilogLogger("global");
        }),
    });
    container
        .resolve(globalLoggerToken)
        .info("Growave main script is initialized!");

    container.register(gwStorefrontAppInfoToken, {
        useFactory: instanceCachingFactory(() => {
            return {
                appName: __APP_NAME__,
                version: __VERSION__,
                currentApp: CURRENT_APP_GROWAVE,
                appProxyPrefix: `${
                    window.Shopify?.routes?.root || "/"
                }apps/ssw`,
                appBaseUrl:
                    __APP_ENV__ === "development"
                        ? "https://app.dev.growave.io"
                        : "https://app.growave.io",
                translationsServiceBaseUrl:
                    __APP_ENV__ === "development"
                        ? "https://translations.dev.growave.io"
                        : "https://translations.growave.io",
            };
        }),
    });

    container.register(localStorageToken, { useValue: localStorage });

    // Additional info data
    const additionalInfoData = getJsonFromScript(GW_ADDITIONAL_INFO_SELECTOR);

    // Is embedded mode
    const isEmbeddedMode = checkIsEmbeddedMode(additionalInfoData);
    container.registerInstance(isEmbeddedModeToken, isEmbeddedMode);

    // ProductIdManager
    container.register(ProductIdFromSdkProvider, {
        useFactory: instanceCachingFactory(() => {
            return new ProductIdFromSdkProvider();
        }),
    });
    container.register(globalProductIdManagerToken, {
        useFactory: instanceCachingFactory(() => {
            const productIdManager = new ProductIdManager();
            if (isEmbeddedMode) {
                productIdManager.registerProvider(
                    new ProductIdFromAdditionalInfoProvider(additionalInfoData)
                );
            } else {
                productIdManager.registerProvider(
                    container.resolve(ProductIdFromSdkProvider)
                );
            }
            return productIdManager;
        }),
    });

    // ProductVariantsIdManager
    container.register(ProductVariantIdFromSdkProvider, {
        useFactory: instanceCachingFactory(() => {
            return new ProductVariantIdFromSdkProvider();
        }),
    });

    container.register(globalProductVariantsIdManagerToken, {
        useFactory: instanceCachingFactory(() => {
            const productVariantsIdManager = new ProductVariantsIdManager();
            if (isEmbeddedMode) {
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromAdditionalInfoProvider(
                        additionalInfoData
                    )
                );
                productVariantsIdManager.registerProvider(
                    new ProductVariantIdFromUrlProvider()
                );
            } else {
                productVariantsIdManager.registerProvider(
                    container.resolve(ProductVariantIdFromSdkProvider)
                );
            }
            return productVariantsIdManager;
        }),
    });

    container.register(SdkModule, {
        useFactory: instanceCachingFactory(() => new SdkModule(initFromSdk)),
    });
    container.resolve(SdkModule).registerProviders();

    if (isEmbeddedMode) {
        void initFromAdditionalInfo(additionalInfoData);
    }
}

__gwMain();
